import { DefaultDateType } from "api/services/models/types";
import dayjs, { Dayjs } from "dayjs";

export const DefaultArchiveFilters = () => {
  const today = dayjs();

  // date ranges
  const startOfWeek = dayjs().startOf("week");
  const endOfWeek = dayjs().endOf("week");
  const startOfLastWeek = dayjs().subtract(1, "week").startOf("week");
  const endOfLastWeek = dayjs().subtract(1, "week").endOf("week");

  const startOfMonth = dayjs().startOf("month");
  const endOfMonth = dayjs().endOf("month");
  const startOfLastMonth = dayjs().subtract(1, "month").startOf("month");
  const endOfLastMonth = dayjs().subtract(1, "month").endOf("month");

  const toDayjs = (dateStr: string) => dayjs(dateStr, "MM/DD/YYYY");

  const createView = (
    type: "DOS" | "DOD",
    from: dayjs.Dayjs,
    to: dayjs.Dayjs,
    label: string,
    defaultDateType: DefaultDateType
  ) => {
    const isDOS = type === "DOS";
    return {
      selectedServiceDateFrom: isDOS ? from.format("MM/DD/YYYY") : "",
      selectedServiceDateTo: isDOS ? to.format("MM/DD/YYYY") : "",
      selectedDictationDateFrom: !isDOS ? from.format("MM/DD/YYYY") : "",
      selectedDictationDateTo: !isDOS ? to.format("MM/DD/YYYY") : "",
      selectedDOB: "",
      viewName: type === "DOS" ? `Date of Service - ${label}` : `Date of Dictation - ${label}`,
      defaultView: true,
      defaultDateType: defaultDateType
    };
  };

  const defaultViews = [
    createView("DOS", startOfWeek, endOfWeek, "Current Week", DefaultDateType.DOSCurrentWeek),
    createView("DOS", startOfLastWeek, endOfLastWeek, "Last Week", DefaultDateType.DOSLastWeek),
    createView("DOS", startOfMonth, endOfMonth, "Current Month", DefaultDateType.DOSCurrentMonth),
    createView("DOS", startOfLastMonth, endOfLastMonth, "Last Month", DefaultDateType.DOSLastMonth),

    createView("DOD", startOfWeek, endOfWeek, "Current Week", DefaultDateType.DODCurrentWeek),
    createView("DOD", startOfLastWeek, endOfLastWeek, "Last Week", DefaultDateType.DODLastWeek),
    createView("DOD", startOfMonth, endOfMonth, "Current Month", DefaultDateType.DODCurrentMonth),
    createView("DOD", startOfLastMonth, endOfLastMonth, "Last Month", DefaultDateType.DODLastMonth),
  ];

  // Load existing views
  let existingViews = JSON.parse(localStorage.getItem("archiveFilterViews")!) || [];

  // Remove views older than 2 months
  existingViews = existingViews.filter((view: any) => {
    if (
      !view.viewName?.startsWith("Date of Service -") &&
      !view.viewName?.startsWith("Date of Dictation -")
    )
      return true;

    const endDateStr =
      view.selectedServiceDateTo || view.selectedDictationDateTo || "";
    if (!endDateStr) return true;

    const endDate = toDayjs(endDateStr);
    const twoMonthsAgo = dayjs().subtract(2, "month").startOf("month");

    return endDate.isAfter(twoMonthsAgo, "day");
  });

  // Replace or add default views
  const upsertView = (newView: any) => {
    const index = existingViews.findIndex(
      (v: any) => v.viewName === newView.viewName
    );
    if (index >= 0) {
      const existing = existingViews[index];
      const endDateStr = existing.selectedServiceDateTo || existing.selectedDictationDateTo || "";
      const endDate = toDayjs(endDateStr);
      if (endDate.isBefore(today, "day")) {
        existingViews[index] = newView;
      }
    } else {
      existingViews.push(newView);
    }
  };

  defaultViews.forEach(upsertView);

  localStorage.setItem("archiveFilterViews", JSON.stringify(existingViews));
};

export const calculateDate = (dateRange: DefaultDateType): {from : Dayjs, to: Dayjs} | null => {

  const startOfWeek = dayjs().startOf("week");
  const endOfWeek = dayjs().endOf("week");
  const startOfLastWeek = dayjs().subtract(1, "week").startOf("week");
  const endOfLastWeek = dayjs().subtract(1, "week").endOf("week");

  const startOfMonth = dayjs().startOf("month");
  const endOfMonth = dayjs().endOf("month");
  const startOfLastMonth = dayjs().subtract(1, "month").startOf("month");
  const endOfLastMonth = dayjs().subtract(1, "month").endOf("month");

  switch (dateRange) {
    case DefaultDateType.DOSCurrentWeek:
    case DefaultDateType.DODCurrentWeek:
      return { from: startOfWeek, to: endOfWeek };  
    case DefaultDateType.DOSLastWeek:
    case DefaultDateType.DODLastWeek:
      return { from: startOfLastWeek, to: endOfLastWeek };
    case DefaultDateType.DOSCurrentMonth:
    case DefaultDateType.DODCurrentMonth:
      return { from: startOfMonth, to: endOfMonth };
    case DefaultDateType.DOSLastMonth:
    case DefaultDateType.DODLastMonth:
      return { from: startOfLastMonth, to: endOfLastMonth };
    default:
      return null;
  }
};